import React from 'react';
import PropTypes from 'prop-types';
import { X, History } from 'lucide-react';
import { formatDateTime } from '../utils/dateUtils';
import { useTimezone } from '../contexts/TimezoneContext';

/**
 * AssessmentHistoryModal - Modal for viewing and selecting historical assessments
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {function} onClose - Handler to close the modal
 * @param {Array} assessmentHistory - Array of historical assessment objects
 * @param {Object} currentAssessment - Currently viewed assessment (to show "Currently Viewing")
 * @param {function} onLoadHistoricalAssessment - Handler when user selects a historical assessment
 */
const AssessmentHistoryModal = ({
  isOpen,
  onClose,
  assessmentHistory,
  currentAssessment,
  onLoadHistoricalAssessment
}) => {
  const { timezone } = useTimezone();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <History className="text-purple-600" size={24} />
            <h3 className="text-xl font-bold text-gray-900">Assessment History</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close modal"
          >
            <X size={24} />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6">
          {assessmentHistory.length === 0 ? (
            <div className="text-center py-12">
              <History className="mx-auto text-gray-400 mb-4" size={48} />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Previous Assessments</h3>
              <p className="text-gray-600">
                You haven't completed any previous assessments yet.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {assessmentHistory.map((hist) => {
                const isCurrentlyViewing = currentAssessment && currentAssessment.assessment_id === hist.assessment_id;
                const completionPercent = hist.total_questions > 0
                  ? (hist.answered_questions / hist.total_questions) * 100
                  : 0;

                return (
                  <div
                    key={hist.assessment_id}
                    className={`border rounded-lg p-4 transition ${
                      isCurrentlyViewing
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h4 className="font-semibold text-gray-900">
                            Assessment from {formatDateTime(hist.created_at, timezone)}
                          </h4>
                          {isCurrentlyViewing && (
                            <span className="px-2 py-1 text-xs font-medium bg-purple-600 text-white rounded">
                              Currently Viewing
                            </span>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-3">
                          <div>
                            <p className="text-sm text-gray-600">Overall Score</p>
                            <p className="text-2xl font-bold text-gray-900">
                              {Math.round(hist.overall_score)}%
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Completion</p>
                            <p className="text-2xl font-bold text-gray-900">
                              {Math.round(completionPercent)}%
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <span>{hist.answered_questions} of {hist.total_questions} questions answered</span>
                          {hist.updated_at && hist.updated_at !== hist.created_at && (
                            <span>Last updated: {formatDateTime(hist.updated_at, timezone)}</span>
                          )}
                        </div>
                      </div>

                      {!isCurrentlyViewing && (
                        <button
                          onClick={() => onLoadHistoricalAssessment(hist.assessment_id)}
                          className="ml-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition whitespace-nowrap min-h-[44px]"
                        >
                          View
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition min-h-[44px]"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

AssessmentHistoryModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  assessmentHistory: PropTypes.arrayOf(
    PropTypes.shape({
      assessment_id: PropTypes.number.isRequired,
      created_at: PropTypes.string.isRequired,
      updated_at: PropTypes.string,
      overall_score: PropTypes.number.isRequired,
      answered_questions: PropTypes.number.isRequired,
      total_questions: PropTypes.number.isRequired
    })
  ).isRequired,
  currentAssessment: PropTypes.shape({
    assessment_id: PropTypes.number
  }),
  onLoadHistoricalAssessment: PropTypes.func.isRequired
};

export default AssessmentHistoryModal;
